import {
  setPosts,
  removePost,
  updatePost as updatePostInStore,
  patchPostReactions,
  patchPostCommentsCount,
} from '../../redux/slices/postsSlice';
import { store } from '../../redux/store';
import { CreateCommentDto, CreatePostDto, Post, PostComment, UpdatePostDto } from '../../types/post';
import httpService from '../httpService';
import { RootState } from '../../redux/reducers';

function getCommunityId(): number {
  const communityId = (store.getState() as RootState).application.selectedCommunity;
  if (!communityId) {
    throw new Error('No community selected');
  }
  return communityId;
}

const postService = {
  getPosts: async (): Promise<Post[]> => {
    const communityId = getCommunityId();
    const { data } = await httpService.get<Post[]>(`/networks/communities/${communityId}/posts`);
    store.dispatch(setPosts({ communityId, posts: data }));
    return data;
  },

  createPost: async (dto: CreatePostDto): Promise<Post> => {
    const communityId = getCommunityId();
    const { data } = await httpService.post<Post>(`/networks/communities/${communityId}/posts`, dto);
    const existing = (store.getState() as RootState).posts.posts[communityId] ?? [];
    store.dispatch(setPosts({ communityId, posts: [data, ...existing] }));
    return data;
  },

  updatePost: async (postId: number, dto: UpdatePostDto): Promise<Post> => {
    const communityId = getCommunityId();
    const { data } = await httpService.put<Post>(
      `/networks/communities/${communityId}/posts/${postId}`,
      dto,
    );
    store.dispatch(updatePostInStore({ communityId, post: data }));
    return data;
  },

  deletePost: async (postId: number): Promise<void> => {
    const communityId = getCommunityId();
    await httpService.delete(`/networks/communities/${communityId}/posts/${postId}`);
    store.dispatch(removePost({ communityId, postId }));
  },

  pinPost: async (postId: number, isPinned: boolean): Promise<Post> => {
    const communityId = getCommunityId();
    const { data } = await httpService.put<Post>(
      `/networks/communities/${communityId}/posts/${postId}/pin`,
      { isPinned },
    );
    store.dispatch(updatePostInStore({ communityId, post: data }));
    return data;
  },

  addReaction: async (postId: number, emoji: string, currentUserId: number): Promise<void> => {
    const communityId = getCommunityId();
    const posts = (store.getState() as RootState).posts.posts[communityId] ?? [];
    const post = posts.find((p) => p.postId === postId);
    if (!post) return;

    const previousReactions = post.reactions;
    const alreadyExists = previousReactions.some(
      (r) => r.userId === currentUserId && r.emoji === emoji,
    );
    if (alreadyExists) return;

    store.dispatch(
      patchPostReactions({
        communityId,
        postId,
        reactions: [...previousReactions, { id: Date.now(), userId: currentUserId, emoji }],
      }),
    );

    try {
      await httpService.post(
        `/networks/communities/${communityId}/posts/${postId}/reactions`,
        { emoji },
      );
    } catch {
      store.dispatch(patchPostReactions({ communityId, postId, reactions: previousReactions }));
      throw new Error('Failed to add reaction');
    }
  },

  removeReaction: async (postId: number, emoji: string, currentUserId: number): Promise<void> => {
    const communityId = getCommunityId();
    const posts = (store.getState() as RootState).posts.posts[communityId] ?? [];
    const post = posts.find((p) => p.postId === postId);
    if (!post) return;

    const previousReactions = post.reactions;
    store.dispatch(
      patchPostReactions({
        communityId,
        postId,
        reactions: previousReactions.filter(
          (r) => !(r.userId === currentUserId && r.emoji === emoji),
        ),
      }),
    );

    try {
      await httpService.delete(
        `/networks/communities/${communityId}/posts/${postId}/reactions`,
        { data: { emoji } },
      );
    } catch {
      store.dispatch(patchPostReactions({ communityId, postId, reactions: previousReactions }));
      throw new Error('Failed to remove reaction');
    }
  },

  getComments: async (postId: number): Promise<PostComment[]> => {
    const communityId = getCommunityId();
    const { data } = await httpService.get<PostComment[]>(
      `/networks/communities/${communityId}/posts/${postId}/comments`,
    );
    return data;
  },

  addComment: async (postId: number, dto: CreateCommentDto): Promise<PostComment> => {
    const communityId = getCommunityId();
    const { data } = await httpService.post<PostComment>(
      `/networks/communities/${communityId}/posts/${postId}/comments`,
      dto,
    );
    const posts = (store.getState() as RootState).posts.posts[communityId] ?? [];
    const post = posts.find((p) => p.postId === postId);
    if (post) {
      store.dispatch(
        patchPostCommentsCount({
          communityId,
          postId,
          commentsCount: post.commentsCount + 1,
        }),
      );
    }
    return data;
  },

  deleteComment: async (postId: number, commentId: number): Promise<void> => {
    const communityId = getCommunityId();
    await httpService.delete(
      `/networks/communities/${communityId}/posts/${postId}/comments/${commentId}`,
    );
    const posts = (store.getState() as RootState).posts.posts[communityId] ?? [];
    const post = posts.find((p) => p.postId === postId);
    if (post) {
      store.dispatch(
        patchPostCommentsCount({
          communityId,
          postId,
          commentsCount: Math.max(0, post.commentsCount - 1),
        }),
      );
    }
  },

  uploadPostImages: async (files: File[]): Promise<string[]> => {
    const communityId = getCommunityId();
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    const { data } = await httpService.post<{ urls: string[] }>(
      `/networks/communities/${communityId}/posts/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return data.urls;
  },
};

export default postService;
